import React from 'react';
import { Dropdown } from 'primereact/dropdown';

// ─── Module-level cache — one fetch shared across ALL instances ───────────────
type InsurerOption = { name: string; id: string };
let _cachedOptions: InsurerOption[] | null = null;
let _fetchPromise: Promise<InsurerOption[]> | null = null;

async function _apiFetch(search: string, top: number = 20): Promise<InsurerOption[]> {
  const ncToken = localStorage.getItem('NCToken') || '';
  const authToken = localStorage.getItem('igtoken') || '';
  const params = new URLSearchParams({ top: String(top), skip: '0' });
  if (search) params.set('search', search);
  const url = `${process.env.REACT_APP_COIPortal_Url}/api/igigcoi/insurers?${params.toString()}`;
  const res = await fetch(url, {
    headers: {
      Accept: 'application/json',
      Authorization: `Bearer ${authToken}`,
      NCToken: ncToken,
    },
  });
  if (!res.ok) return [];
  const data = await res.json();
  const items: any[] = data.value ?? data ?? [];
  return items
    .map((item: any) => ({
      name: (item.companyName ?? item.name ?? '') as string,
      id:   (item.databaseId ?? '') as string,
    }))
    .filter(o => o.name);
}

function loadDefaults(): Promise<InsurerOption[]> {
  if (_cachedOptions) return Promise.resolve(_cachedOptions);
  if (_fetchPromise) return _fetchPromise;
  _fetchPromise = _apiFetch('').then(opts => {
    _cachedOptions = opts;
    return opts;
  });
  return _fetchPromise;
}

/**
 * NowCerts appends the carrier customerId in parentheses to carrier names
 * (e.g. "21st Century Insurance (2356)"). Strip that suffix so lookups work
 * against the plain names returned by the insurer dropdown API.
 */
function stripCarrierSuffix(name: string): string {
  return name.replace(/\s*\(\w+\)\s*$/, '').trim();
}

function findInOptions(opts: InsurerOption[], name: string): InsurerOption | undefined {
  const exact = opts.find(o => o.name === name);
  if (exact) return exact;
  const stripped = stripCarrierSuffix(name);
  if (stripped !== name) return opts.find(o => o.name === stripped);
  return undefined;
}

/**
 * Synchronous lookup from the already-loaded cache.
 * Returns null if the cache hasn't loaded yet or name isn't found.
 * Use this inside getFormData() to avoid async timing issues.
 */
export function lookupCarrierIdSync(name: string): string | null {
  if (!name || !_cachedOptions) return null;
  return findInOptions(_cachedOptions, name)?.id ?? null;
}

export async function resolveCarrierIdByName(name: string): Promise<string | null> {
  if (!name) return null;
  const opts = await loadDefaults();
  const cached = findInOptions(opts, name);
  if (cached) return cached.id;
  // Not in the initial 20 — search the API using the stripped name
  const searchName = stripCarrierSuffix(name);
  const results = await _apiFetch(searchName);
  return findInOptions(results, name)?.id ?? null;
}

// Full carrier list, fetched once on demand for id→name resolution (a stored
// naiCid can point to any carrier in the directory, not just the first 20).
let _fullOptionsPromise: Promise<InsurerOption[]> | null = null;
function loadAllOptions(): Promise<InsurerOption[]> {
  if (!_fullOptionsPromise) _fullOptionsPromise = _apiFetch('', 5000);
  return _fullOptionsPromise;
}

/** Resolves a carrier databaseId (e.g. a certificate's stored naiCid) back to its name. */
export async function resolveCarrierNameById(id: string): Promise<string | null> {
  if (!id) return null;
  const cachedHit = _cachedOptions?.find(o => o.id === id);
  if (cachedHit) return cachedHit.name;
  const all = await loadAllOptions();
  return all.find(o => o.id === id)?.name ?? null;
}
// ─────────────────────────────────────────────────────────────────────────────

interface InsurerSearchDropdownProps {
  value: string;
  onChange: (name: string) => void;
  /** Called with the carrier databaseId when an option is selected from the list, or null when text is typed directly. */
  onIdChange?: (id: string | null) => void;
  className?: string;
}

const DEBOUNCE_MS = 300;

/** Merge two option arrays, deduplicating by id and name. */
function mergeOptions(base: InsurerOption[], incoming: InsurerOption[]): InsurerOption[] {
  const seenIds = new Set(base.map(o => o.id));
  const seenNames = new Set(base.map(o => o.name.toLowerCase()));
  return [
    ...base,
    ...incoming.filter(o => {
      if (seenIds.has(o.id)) return false;
      if (seenNames.has(o.name.toLowerCase())) return false;
      seenIds.add(o.id);
      seenNames.add(o.name.toLowerCase());
      return true;
    }),
  ];
}

const InsurerSearchDropdown: React.FC<InsurerSearchDropdownProps> = ({
  value,
  onChange,
  onIdChange,
  className,
}) => {
  const [options, setOptions] = React.useState<InsurerOption[]>([]);
  // Accumulates every name→id pair ever loaded (cache + API searches) so
  // handleChange can resolve IDs even when `options` is a filtered subset.
  const allKnownRef = React.useRef<Map<string, string>>(new Map());
  const addToKnown = (items: InsurerOption[]) => {
    items.forEach(o => { if (o.name && o.id) allKnownRef.current.set(o.name, o.id); });
  };
  // Track the active filter query so async callbacks can filter by the latest value
  const activeQueryRef = React.useRef('');
  const debounceRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  // Load default 20 once on first mount (shared cache — only 1 API call total)
  React.useEffect(() => {
    loadDefaults().then(opts => { addToKnown(opts); setOptions(opts); });
  }, []);

  const handleChange = (e: { value: string }) => {
    const name = e.value ?? '';
    // Use allKnownRef so ID resolution works even when options is a filtered subset
    const id = allKnownRef.current.get(name) ?? null;
    onChange(name);
    onIdChange?.(id);
  };

  // Reset options to the full default list every time the panel opens
  const handleShow = () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    activeQueryRef.current = '';
    setOptions(_cachedOptions ?? []);
  };

  // Fires when user types in the filter search box inside the dropdown panel
  const handleFilter = (e: { filter: string }) => {
    const raw = e.filter ?? '';
    const query = raw.trim().toLowerCase();

    // Always track the latest query so async callbacks can use it
    activeQueryRef.current = query;

    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (!query) {
      // Restore full cached list
      setOptions(_cachedOptions ?? []);
      return;
    }

    // 1. Instant local filter from cache so the list reacts immediately
    const localFiltered = (_cachedOptions ?? []).filter(o =>
      o.name.toLowerCase().includes(query)
    );
    setOptions(localFiltered);

    // 2. Debounced API call — merge with cache, then re-filter by the CURRENT query
    //    so the list stays filtered even after React re-renders from setOptions
    debounceRef.current = setTimeout(async () => {
      const remote = await _apiFetch(query);
      addToKnown(remote);
      const currentQuery = activeQueryRef.current;
      // Merge remote results into the cache without replacing it
      const merged = mergeOptions(_cachedOptions ?? [], remote);
      // Always filter the final list by whatever query is active right now
      const filtered = merged.filter(o =>
        o.name.toLowerCase().includes(currentQuery)
      );
      setOptions(filtered);
    }, DEBOUNCE_MS);
  };

  return (
    <Dropdown
      value={value}
      options={options}
      optionLabel="name"
      optionValue="name"
      onChange={handleChange}
      onFilter={handleFilter}
      onShow={handleShow}
      filter
      editable
      placeholder=""
      className={className}
      style={{ width: '100%' }}
      emptyMessage="No carriers found"
      emptyFilterMessage="No carriers found"
    />
  );
};

export default InsurerSearchDropdown;
